/**
 * Utility functions for Source Review components
 */

import { SourceReviewStatus } from 'src/generated/graphql';
import { formatSourceReviewDateTime } from '../common/util/TimestampHelpers';

export enum ReviewStatus {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

/**
 * Extracts a subset of data to show on a specific page based on pagination parameters.
 *
 * @param data - Array of items to paginate
 * @param page - Current page number
 * @param itemsPerPage - Page size
 * @returns Array slice containing items for the requested page
 */
export const getPaginatedData = (
  data: any[],
  page: number,
  itemsPerPage: number
) => {
  if (!data || !data.length) {
    return [];
  }
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return data.slice(startIndex, endIndex);
};

/**
 * Calculates the display range string showing which items are visible on the current page.
 *
 * @param currentPage - Current page number
 * @param itemsPerPage - Page Size
 * @param totalItems - Total number of items across all pages
 * @returns Formatted string in the format "start-end of total" (e.g., "1-10 of 50")
 */
export const getDisplayRange = (
  currentPage: number,
  itemsPerPage: number,
  totalItems: number
) => {
  const start = (currentPage - 1) * itemsPerPage + 1;
  const end = Math.min(currentPage * itemsPerPage, totalItems);
  return `${start}-${end} of ${totalItems}`;
};

/**
 * Compares two objects by a specified property for sorting purposes.
 *
 * @param property - Property name for comparison
 * @param direction - Sort direction ('asc' or 'desc')
 * @param isTimestamp - Whether the property contains timestamp values
 * @returns Comparison result for sorting
 */
export const compareValues = (
  a: any,
  b: any,
  property: string,
  direction: 'asc' | 'desc',
  isTimestamp = false
) => {
  const aVal = a[property];
  const bVal = b[property];

  if (aVal == null && bVal == null) return 0;
  if (aVal == null) return 1;
  if (bVal == null) return -1;

  if (isTimestamp) {
    const aTimestamp = parseInt(aVal, 10) || 0;
    const bTimestamp = parseInt(bVal, 10) || 0;
    const comparison = aTimestamp - bTimestamp;
    return direction === 'desc' ? -comparison : comparison;
  }

  const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
  return direction === 'desc' ? -comparison : comparison;
};

/**
 * Transforms an open review row for display in LUI
 *
 * @param row - Open review data object
 * @param language - Language code for timestamp localization
 * @returns New object with original data plus formatted timestamp display field
 */
export const formatOpenReviewRow = (row: any, language: string) => {
  if (!row) return null;
  return {
    ...row,
    sourceReviewRequestedTimestampDisplay: formatSourceReviewDateTime(
      row.sourceReviewRequestedTimestamp,
      true,
      language
    ),
  };
};

/**
 * Transforms a completed review row for display in LUI
 *
 * @param row - Completed review data object
 * @param language - Language code for timestamp localization
 * @returns New object with original data plus formatted timestamp display fields
 */
export const formatCompletedReviewRow = (row: any, language: string) => {
  if (!row) return null;
  return {
    ...row,
    sourceReviewRequestedTimestampDisplay: formatSourceReviewDateTime(
      row.sourceReviewRequestedTimestamp,
      true,
      language
    ),
    updateTimestampDisplay: formatSourceReviewDateTime(
      row.updateTimestamp,
      true,
      language
    ),
    sourceReviewStatusDisplay:
      row.sourceReviewStatus === SourceReviewStatus.Complete
        ? ReviewStatus.APPROVED
        : row.sourceReviewStatus === SourceReviewStatus.Discard
        ? ReviewStatus.REJECTED
        : row.sourceReviewStatus,
  };
};

/**
 * Creates a phonetool URL for a given user alias
 *
 * @param alias - User alias
 * @returns Phonetool URL for the user
 */
export const getPhonetoolUrl = (alias: string) => {
  return `https://phonetool.amazon.com/users/${alias}`;
};
